import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
// Les formateurs de refus viennent du store d'authentification : c'est lui qui parle
// déjà aux routes /api/auth, et un même refus doit se lire pareil sur l'écran de
// connexion et dans ce panneau.
import { useAuthStore, lireJson, formatDetailApi, messageUtilisateur } from '../store/useAuthStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8090';

// Le serveur ne fournit que la cause, souvent en trois mots (« Session
// introuvable ») : on y accole toujours la conduite à tenir, sans quoi
// l'utilisateur lit un constat sans savoir s'il doit recommencer.
const avecConduite = (cause, conduite) => {
    const nette = cause ? String(cause).trim().replace(/[.\s]+$/, '') : '';
    return nette ? `${nette}. ${conduite}` : conduite;
};

// Les 401 et 403 de ce panneau ne sortent PAS des routes /api/auth, qui répondent en
// français : ils viennent de la dépendance d'authentification, dont le `detail` est
// en anglais (« Invalid or expired token », « Not authenticated »). Et comme ce
// panneau appelle `fetch` avec l'en-tête d'autorisation au lieu de passer par le
// client qui rafraîchit le jeton, le jeton d'accès expiré (120 min) est le refus le
// plus courant ici. Le bandeau affichait donc une phrase à moitié anglaise. Sur ces
// deux codes on ignore le serveur et on écrit la conduite à tenir en français.
const estRefusAuth = (statut) => statut === 401 || statut === 403;

export default function UserProfileModal({ isOpen, onClose }) {
    const { user, accessToken, refreshToken, logout, updateUser } = useAuthStore();
    const [sessions, setSessions] = useState([]);
    // Sait-on identifier la session courante sur ce navigateur ? Faux, la
    // révocation est retirée : cf. fetchSessions.
    const [identificationSure, setIdentificationSure] = useState(true);
    const [storage, setStorage] = useState(null);
    const [editUsername, setEditUsername] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const authHeaders = { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        if (isOpen && accessToken) {
            setEditUsername(user?.username || '');
            setAvatarUrl(user?.avatar_url || '');
            setMessage('');
            setError('');
            setShowPasswordForm(false);
            fetchSessions();
            fetchStorage();
        }
    }, [isOpen]);

    const sha256Hex = async (text) => {
        if (!window.crypto?.subtle) return null;
        const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
        return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
    };

    // C'est ici que se reconnaît notre propre session. Le serveur ne peut pas
    // la deviner et répondait « courante » pour TOUTES les sessions : aucune
    // n'était donc jamais vue comme « autre », le bouton « Tout déconnecter »
    // n'apparaissait jamais et le « Révoquer » des autres appareils restait
    // hors d'atteinte. On compare l'empreinte publiée par le serveur à celle
    // du jeton de rafraîchissement que ce navigateur seul détient.
    const fetchSessions = async () => {
        try {
            const res = await fetch(`${API_URL}/api/auth/sessions`, { headers: authHeaders });
            if (!res.ok) return;
            const data = await res.json();
            const myFingerprint = refreshToken ? await sha256Hex(refreshToken) : null;
            // Sans empreinte — `crypto.subtle` absent hors contexte sécurisé, ou
            // jeton de rafraîchissement manquant — on ne SAIT PAS laquelle est la
            // session courante. Marquer toutes les lignes « autre » afficherait
            // « Révoquer » sur l'appareil en cours, et un clic déconnecterait
            // l'utilisateur de sa propre session. On préfère alors ne rien offrir.
            setIdentificationSure(Boolean(myFingerprint));
            setSessions((data.sessions || []).map(s => ({
                ...s,
                is_current: Boolean(myFingerprint) && s.token_fingerprint === myFingerprint,
            })));
        } catch (e) { /* ignore */ }
    };

    const fetchStorage = async () => {
        try {
            const res = await fetch(`${API_URL}/api/me/storage`, { headers: authHeaders });
            if (res.ok) { const data = await res.json(); setStorage(data); }
        } catch (e) { /* ignore */ }
    };

    const handleUpdateProfile = async () => {
        setError(''); setMessage('');
        try {
            const body = {};
            if (editUsername !== user?.username) body.username = editUsername;
            if (avatarUrl !== (user?.avatar_url || '')) body.avatar_url = avatarUrl || null;
            if (Object.keys(body).length === 0) return;

            const res = await fetch(`${API_URL}/api/auth/me`, { method: 'PUT', headers: authHeaders, body: JSON.stringify(body) });
            const data = await lireJson(res);
            if (!res.ok) {
                throw new Error(estRefusAuth(res.status)
                    ? "Votre session a expiré : votre profil n'a pas été enregistré. Déconnectez-vous, reconnectez-vous, puis enregistrez de nouveau."
                    : avecConduite(
                        formatDetailApi(data?.detail, `Votre profil n'a pas pu être enregistré (erreur ${res.status})`),
                        res.status === 409 ? "Choisissez un autre nom d'utilisateur, puis enregistrez de nouveau."
                            : res.status === 422 ? 'Corrigez le champ signalé, puis enregistrez de nouveau.'
                                : 'Réessayez dans un instant.'));
            }
            if (!data) throw new Error('Votre profil a peut-être été enregistré, mais la réponse du serveur est illisible. Rouvrez ce panneau pour vérifier.');
            updateUser(data);
            setMessage('Profil mis à jour');
        } catch (e) { setError(messageUtilisateur(e, "Votre profil n'a pas pu être enregistré. Réessayez dans un instant.")); }
    };

    const handleChangePassword = async () => {
        setError(''); setMessage('');
        try {
            const res = await fetch(`${API_URL}/api/auth/me/password`, {
                method: 'PUT', headers: authHeaders,
                body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
            });
            if (!res.ok) {
                const data = await lireJson(res);
                // Le mot de passe actuel refusé est un 400, jamais un 401 : ce 401
                // était rangé avec lui et disait « vérifiez votre mot de passe
                // actuel » alors que c'est la session qui avait expiré.
                throw new Error(estRefusAuth(res.status)
                    ? "Votre session a expiré : votre mot de passe n'a pas été changé. Déconnectez-vous, reconnectez-vous, puis réessayez."
                    : avecConduite(
                        formatDetailApi(data?.detail, `Votre mot de passe n'a pas pu être changé (erreur ${res.status})`),
                        res.status === 400
                            ? 'Vérifiez votre mot de passe actuel, puis réessayez.'
                            : res.status === 422 ? 'Le nouveau mot de passe doit faire au moins 8 caractères, avec 1 majuscule, 1 chiffre et 1 caractère spécial.'
                                : 'Réessayez dans un instant.'));
            }
            setMessage('Mot de passe changé avec succès');
            setShowPasswordForm(false);
            setCurrentPassword(''); setNewPassword('');
        } catch (e) { setError(messageUtilisateur(e, "Votre mot de passe n'a pas pu être changé. Réessayez dans un instant.")); }
    };

    // On annonçait le bandeau vert sans regarder `res.ok` : un 403 ou un 500
    // s'affichait comme un succès, et l'utilisateur repartait convaincu que les
    // autres appareils étaient déconnectés alors que rien n'avait bougé.
    const handleRevokeAll = async () => {
        setError(''); setMessage('');
        try {
            const res = await fetch(`${API_URL}/api/auth/sessions`, {
                method: 'DELETE', headers: authHeaders,
                body: JSON.stringify({ refresh_token: refreshToken }),
            });
            if (!res.ok) {
                const data = await lireJson(res);
                throw new Error(estRefusAuth(res.status)
                    ? 'Votre session a expiré : les autres sessions sont toujours ouvertes. Déconnectez-vous, reconnectez-vous, puis réessayez.'
                    : avecConduite(
                        formatDetailApi(data?.detail, `Les autres sessions n'ont pas pu être déconnectées (erreur ${res.status})`),
                        'Elles sont toujours ouvertes : réessayez dans un instant.'));
            }
            setMessage('Toutes les autres sessions ont été déconnectées');
        } catch (e) {
            setError(messageUtilisateur(e, "Les autres sessions n'ont pas pu être déconnectées. Elles sont toujours ouvertes : réessayez dans un instant."));
        }
        // La liste est rechargée dans les deux cas : après un refus elle doit
        // montrer l'état réel du serveur, pas celui qu'on espérait.
        fetchSessions();
    };

    // Un refus (404 « Session introuvable », 403) était totalement muet : la liste
    // se rechargeait à l'identique et l'utilisateur croyait avoir révoqué l'appareil.
    const handleRevokeOne = async (sessionId) => {
        setError(''); setMessage('');
        try {
            const res = await fetch(`${API_URL}/api/auth/sessions/${sessionId}`, { method: 'DELETE', headers: authHeaders });
            if (!res.ok) {
                const data = await lireJson(res);
                throw new Error(estRefusAuth(res.status)
                    ? 'Votre session a expiré : cette session est toujours ouverte. Déconnectez-vous, reconnectez-vous, puis réessayez.'
                    : avecConduite(
                        formatDetailApi(data?.detail, `Cette session n'a pas pu être révoquée (erreur ${res.status})`),
                        res.status === 404
                            ? "Elle était déjà fermée : la liste vient d'être rafraîchie."
                            : 'Elle est toujours ouverte : réessayez dans un instant.'));
            }
            setMessage('Session révoquée');
        } catch (e) {
            setError(messageUtilisateur(e, "Cette session n'a pas pu être révoquée. Elle est toujours ouverte : réessayez dans un instant."));
        }
        fetchSessions();
    };

    const handleLogout = async () => {
        await logout();
        window.location.reload();
    };

    const timeAgo = (dateStr) => {
        if (!dateStr) return '';
        const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
        if (diff < 60) return 'à l\'instant';
        if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
        if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
        return `il y a ${Math.floor(diff / 86400)}j`;
    };

    const formatBytes = (bytes) => {
        if (bytes < 1024) return `${bytes} o`;
        if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
        if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} Mo`;
        return `${(bytes / 1073741824).toFixed(2)} Go`;
    };

    if (!isOpen) return null;

    const initial = user?.username?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?';

    return createPortal(
        // modal-backdrop = sombre fixe ; pas de onClick = fermeture via croix.
        <div className="fixed inset-0 z-[140] flex items-center justify-center modal-backdrop backdrop-blur-md p-6 font-mono">
            <div className="bg-forge-bg/95 backdrop-blur-md border border-forge-border/25 w-full max-w-[560px] rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.5)] overflow-hidden max-h-[88vh] flex flex-col"
                 onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-forge-border/15 bg-black/20">
                    <h2 className="text-forge-text text-[13px] font-bold uppercase tracking-[0.2em]">Mon Profil</h2>
                    <button onClick={onClose}
                        className="w-7 h-7 rounded-md bg-black/30 border border-white/10 text-forge-muted hover:text-forge-rose hover:border-forge-rose/30 flex items-center justify-center transition-colors">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>

                <div className="px-6 py-5 space-y-5 overflow-y-auto custom-scrollbar">

                    {/* Messages */}
                    {message && <div className="bg-green-500/15 border border-green-500/40 rounded-lg px-4 py-2 text-green-300 text-xs">{message}</div>}
                    {error && <div className="bg-red-500/15 border border-red-500/40 rounded-lg px-4 py-2 text-red-300 text-xs">{error}</div>}

                    {/* Avatar + Username */}
                    <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl text-white font-bold shrink-0 shadow-lg overflow-hidden bg-gradient-to-br from-forge-mauve to-forge-mint">
                            {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" /> : initial}
                        </div>
                        {/* Ces champs vivaient dans un simple <div> : la touche Entrée ne
                            déclenchait rien, il fallait viser « Sauvegarder » à la souris.
                            Un vrai <form> rend Entrée équivalente au clic, et c'est lui
                            qui fait appliquer les bornes ci-dessous avant l'envoi. */}
                        <form className="flex-1 space-y-2" onSubmit={e => { e.preventDefault(); handleUpdateProfile(); }}>
                            <div>
                                <label className="text-[9px] uppercase tracking-widest text-forge-muted font-bold">Nom d'utilisateur</label>
                                {/* Bornes recopiées de UpdateProfileRequest (backend/app/schemas_auth.py) :
                                    username min 3 / max 50 et lettres, chiffres, tirets et
                                    underscores seulement. Sans elles, un nom de 2 caractères
                                    partait au serveur et revenait en 422 sans que l'utilisateur
                                    sache quelle règle il venait d'enfreindre. */}
                                <input value={editUsername} onChange={e => setEditUsername(e.target.value)}
                                    required minLength={3} maxLength={50}
                                    pattern="[a-zA-Z0-9_\-]+"
                                    title="3 à 50 caractères : lettres, chiffres, tirets et underscores uniquement."
                                    className="w-full bg-black/30 border border-forge-border/30 text-forge-text rounded-lg px-3 py-2 text-sm outline-none mt-1 focus:border-forge-mauve/50 transition-colors" />
                            </div>
                            <div>
                                <label className="text-[9px] uppercase tracking-widest text-forge-muted font-bold">Email</label>
                                <div className="text-sm text-forge-text/70 mt-1">{user?.email}</div>
                            </div>
                            <div>
                                <label className="text-[9px] uppercase tracking-widest text-forge-muted font-bold">URL photo de profil</label>
                                {/* L'avatar reste facultatif côté serveur (champ nullable) : pas
                                    de `required` ici, mais la longueur maximale de 500 du même
                                    schéma, et le https:// qu'il exige. */}
                                <input value={avatarUrl} onChange={e => setAvatarUrl(e.target.value)} placeholder="https://..."
                                    maxLength={500}
                                    pattern="https://.*"
                                    title="L'adresse doit commencer par https:// et faire au plus 500 caractères."
                                    className="w-full bg-black/30 border border-forge-border/30 text-forge-text rounded-lg px-3 py-2 text-xs outline-none mt-1 focus:border-forge-mauve/50 transition-colors placeholder-forge-muted/40" />
                            </div>
                            <button type="submit"
                                className="px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] transition-all bg-forge-mauve/15 text-forge-mauve border border-forge-mauve/30 hover:bg-forge-mauve/25 hover:border-forge-mauve/50">
                                Sauvegarder
                            </button>
                        </form>
                    </div>

                    {/* Stockage */}
                    {storage && (
                        <div className="bg-black/20 border border-forge-border/20 rounded-xl p-4">
                            <h3 className="text-[9px] uppercase tracking-widest text-forge-muted font-bold mb-3">Stockage</h3>
                            <div className="flex justify-between text-xs mb-2 text-forge-text/80">
                                <span>{formatBytes(storage.used_bytes)} utilisés</span>
                                <span>{formatBytes(storage.quota_bytes)} total</span>
                            </div>
                            <div className="w-full h-2 rounded-full overflow-hidden bg-black/40 border border-white/5">
                                <div className="h-full rounded-full transition-all duration-500"
                                    style={{
                                        width: `${Math.min(storage.used_percent, 100)}%`,
                                        background: storage.used_percent > 90 ? '#ef4444' : storage.used_percent > 70 ? '#f97316' : 'linear-gradient(90deg, rgb(var(--forge-mauve)), rgb(var(--forge-mint)))'
                                    }} />
                            </div>
                            <div className="text-[10px] text-forge-muted mt-2">
                                {storage.project_count} projet{storage.project_count !== 1 ? 's' : ''} &middot; {storage.used_percent}% utilisé
                            </div>
                        </div>
                    )}

                    {/* Sessions */}
                    <div className="bg-black/20 border border-forge-border/20 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-[9px] uppercase tracking-widest text-forge-muted font-bold">Sessions actives</h3>
                            {sessions.filter(s => !s.is_current).length > 0 && (
                                <button onClick={handleRevokeAll}
                                    className="text-[9px] text-forge-rose hover:text-red-300 font-bold uppercase tracking-widest transition-colors">
                                    Tout déconnecter
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {sessions.length === 0 && <div className="text-xs text-forge-muted/60">Aucune session active</div>}
                            {sessions.map(s => (
                                <div key={s.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg border ${s.is_current ? 'bg-forge-mauve/10 border-forge-mauve/30' : 'bg-black/20 border-forge-border/20'}`}>
                                    <div className={`w-2 h-2 rounded-full shrink-0 ${s.is_current ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.6)]' : 'bg-forge-muted/40'}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-forge-text/85 truncate">{s.device_info?.substring(0, 50) || 'Appareil inconnu'}</div>
                                        <div className="text-[10px] text-forge-muted">{s.ip_address} &middot; {timeAgo(s.created_at)}</div>
                                    </div>
                                    {s.is_current ? (
                                        <span className="text-[8px] bg-green-500/20 text-green-300 px-2 py-0.5 rounded font-bold uppercase tracking-widest">Cette session</span>
                                    ) : (
                                        identificationSure ? (
                                            <button onClick={() => handleRevokeOne(s.id)} className="text-[9px] text-forge-rose hover:text-red-300 transition-colors uppercase tracking-widest font-bold">Révoquer</button>
                                        ) : (
                                            <span className="text-[9px] text-forge-muted/40 uppercase tracking-widest" title="Impossible d'identifier votre session courante sur ce navigateur : la révocation est désactivée pour ne pas vous déconnecter par erreur.">—</span>
                                        )
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Sécurité */}
                    <div className="bg-black/20 border border-forge-border/20 rounded-xl p-4">
                        <h3 className="text-[9px] uppercase tracking-widest text-forge-muted font-bold mb-3">Sécurité</h3>
                        {!showPasswordForm ? (
                            <button onClick={() => setShowPasswordForm(true)}
                                className="w-full py-2 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] transition-all bg-forge-lemon/15 text-forge-lemon border border-forge-lemon/30 hover:bg-forge-lemon/25 hover:border-forge-lemon/50">
                                Changer mon mot de passe
                            </button>
                        ) : (
                            /* Les deux champs de mot de passe n'étaient pas dans un <form> et
                               aucun gestionnaire de touche n'existait : la touche Entrée,
                               réflexe de tout formulaire de connexion, ne faisait rien du
                               tout et il fallait aller cliquer « Confirmer » à la souris.
                               « Annuler » est explicitement `type="button"` pour ne pas
                               devenir un second bouton d'envoi. */
                            <form className="space-y-3" onSubmit={e => { e.preventDefault(); handleChangePassword(); }}>
                                <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                                    placeholder="Mot de passe actuel" required
                                    className="w-full bg-black/30 border border-forge-border/30 text-forge-text rounded-lg px-3 py-2 text-sm outline-none focus:border-forge-lemon/50 transition-colors placeholder-forge-muted/40" />
                                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                                    placeholder="Nouveau mot de passe (min 8, 1 chiffre, 1 spécial)" required
                                    className="w-full bg-black/30 border border-forge-border/30 text-forge-text rounded-lg px-3 py-2 text-sm outline-none focus:border-forge-lemon/50 transition-colors placeholder-forge-muted/40" />
                                <div className="flex gap-2">
                                    <button type="submit"
                                        className="flex-1 py-2 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] transition-all bg-forge-lemon/20 text-forge-lemon border border-forge-lemon/40 hover:bg-forge-lemon/30">
                                        Confirmer
                                    </button>
                                    <button type="button" onClick={() => { setShowPasswordForm(false); setCurrentPassword(''); setNewPassword(''); }}
                                        className="px-4 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-forge-muted hover:text-forge-text transition-colors">
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-forge-border/15 bg-black/20">
                    <button onClick={handleLogout}
                        className="w-full py-2.5 bg-forge-rose/15 text-forge-rose border border-forge-rose/30 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-forge-rose/25 hover:border-forge-rose/50 transition-all flex items-center justify-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                        Déconnexion
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
